import React, { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

/**
 * QC Record Modal — used from GRN, Outward Dispatch, and SRN screens.
 *
 * Props: {open, onOpenChange, kind: "grn_inward"|"outward"|"srn",
 *          sourceId, sourceRef, onCreated?}
 */
export default function QCRecordModal({ open, onOpenChange, kind, sourceId, sourceRef, onCreated }) {
  const [form, setForm] = useState({
    inspected_qty: 0, passed_qty: 0, failed_qty: 0, rework_qty: 0,
    outcome: "pass", notes: "", inspector_name: "",
  });
  const [busy, setBusy] = useState(false);

  const counted = Number(form.passed_qty || 0) + Number(form.failed_qty || 0) + Number(form.rework_qty || 0);
  const inspected = Number(form.inspected_qty || 0);
  const overflow = counted > inspected;
  const underflow = counted > 0 && counted < inspected;

  const save = async () => {
    if (overflow) return toast.error(`Counters (${counted}) exceed Inspected (${inspected}). Reconcile before saving.`);
    setBusy(true);
    try {
      const r = await api.post("/ops/qc/records", { ...form, kind, source_id: sourceId, source_ref: sourceRef });
      toast.success(`QC recorded · ${form.outcome.toUpperCase()}${r.data.record.hold_stock ? " · stock held" : ""}`);
      onCreated?.(r.data.record);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "QC save failed"); }
    setBusy(false);
  };

  const KIND_LABEL = { grn_inward: "GRN Inward QC", outward: "Outward Dispatch QC", srn: "SRN Acceptance QC" };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="qc-record-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-accent" /> {KIND_LABEL[kind] || "QC Record"}
          </DialogTitle>
          {sourceRef && <div className="text-[11px] font-mono text-muted-foreground">Source: {sourceRef}</div>}
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div><Label>Inspected qty</Label>
            <Input type="number" value={form.inspected_qty} onChange={e => setForm({...form, inspected_qty: Number(e.target.value)})}
                     className="rounded-sm mt-1 h-10 text-right tabular-nums" data-testid="qc-inspected" /></div>
          <div><Label>Passed</Label>
            <Input type="number" value={form.passed_qty} onChange={e => setForm({...form, passed_qty: Number(e.target.value)})}
                     className="rounded-sm mt-1 h-10 text-right tabular-nums" data-testid="qc-passed" /></div>
          <div><Label>Failed</Label>
            <Input type="number" value={form.failed_qty} onChange={e => setForm({...form, failed_qty: Number(e.target.value)})}
                     className="rounded-sm mt-1 h-10 text-right tabular-nums" data-testid="qc-failed" /></div>
          <div><Label>Rework</Label>
            <Input type="number" value={form.rework_qty} onChange={e => setForm({...form, rework_qty: Number(e.target.value)})}
                     className="rounded-sm mt-1 h-10 text-right tabular-nums" data-testid="qc-rework" /></div>
          <div><Label>Outcome</Label>
            <Select value={form.outcome} onValueChange={v => setForm({...form, outcome: v})}>
              <SelectTrigger className="rounded-sm mt-1 h-10" data-testid="qc-outcome"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pass">Pass</SelectItem>
                <SelectItem value="fail">Fail — hold stock</SelectItem>
                <SelectItem value="rework">Rework</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
              </SelectContent>
            </Select></div>
          <div><Label>Inspector</Label>
            <Input value={form.inspector_name} onChange={e => setForm({...form, inspector_name: e.target.value})}
                     className="rounded-sm mt-1 h-10" data-testid="qc-inspector" /></div>
          <div className="col-span-2"><Label>Notes</Label>
            <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                     className="rounded-sm mt-1 h-10" data-testid="qc-notes" /></div>
        </div>

        {(overflow || underflow) && (
          <div className={`mt-3 rounded-sm border px-3 py-2 text-xs flex items-center justify-between ${overflow ? "bg-red-500/10 border-red-500/30 text-red-700" : "bg-amber-500/10 border-amber-500/30 text-amber-700"}`}
                 data-testid="qc-qty-warn">
            <span>
              {overflow
                ? `Passed + Failed + Rework (${counted}) exceeds Inspected (${inspected}). Fix counters before saving.`
                : `Passed + Failed + Rework (${counted}) is less than Inspected (${inspected}). ${inspected - counted} unit(s) unaccounted for.`}
            </span>
            <span className="font-mono tabular-nums">{counted}/{inspected}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={busy || overflow}
                    className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="qc-modal-save">
            {busy ? "Saving…" : "Record QC"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
